/**
 * Needs stream (plan 443, tech spec 270 §Frontend — M1): the ordered "Needs
 * you now" list on Pro Home, built client-side from data the page has already
 * loaded. Pure: no React, no fetching, no clock of its own (`now` is injected).
 *
 * The Home badge is `count`, so the badge and the list cannot disagree. Rows
 * carry data and an action target; the copy ("Expires in 9h", "Review · $250")
 * belongs to the component that renders them.
 *
 * Inputs and what they contribute:
 *   requests  pending marketplace requests (id, expires_at, fee quote)  -> `request`
 *   summary   Home summary: held connections, recovery windows, offers
 *             expiring within 24h                                       -> `held`, `recovery`, `offer_expiring`
 *   today     Today queue: "new" signals, "high_intent" signals and
 *             "your_followups" tasks                                    -> `new_buyer`, `high_intent`, `follow_up`
 *
 * Aggregate counts (`summary.requests.pending`, `today.marketplaceRequests`)
 * never become rows: they name no request, so the same request reported by
 * both sources can only ever appear once, through `requests`. A `null` summary
 * section (gate off, loading, or listed in `sections_failed`) contributes
 * nothing. Excluded by design (they feed "Also on your book"): readiness-moved
 * and timing groups, automation drafts, snoozed signals, finished tasks.
 */
import type { MarketplaceConnectionRequest } from "./api";
import { isOwnClientRequest, requestBuyerLabel } from "./own-client-request";
import type { ProHomeSummary } from "./pro-home-types";
import { personRoute } from "./routes";
import type { ProvenanceKind } from "../components/home/ProvenanceChip";
import type { NeedsRowKind } from "../components/home/NeedsRow";
import type { TodayQueueVM, TodaySignalVM, TodayTaskItemVM } from "./view-model";

export type { NeedsRowKind };

/** `client` is a row about an existing, non-marketplace client (a recovery window). */
export type NeedsProvenance = ProvenanceKind | "client";

export interface NeedsStreamRow {
  kind: NeedsRowKind;
  /** Stable id within `kind`: request / connection / window / offer / signal item key / task id. */
  ref: string;
  /** ISO instant; null when the source has none (an undated follow-up). */
  deadline: string | null;
  urgent: boolean;
  provenance: NeedsProvenance;
  /**
   * Display handle only: the passport display code for marketplace rows, the
   * pro's own client name for recovery and signal rows, the task title for
   * follow-ups. Never a competing agent and never buyer identity beyond what
   * the source already shows the pro.
   */
  title: string;
  /** Internal route for the row's primary action. */
  action: { href: string };
  /**
   * `request`: the fee the pro is charged on confirm. Absent on an own-client
   * request — its $0 retention quote is not a charge, and a renderer must
   * never format it as "$0".
   */
  feeCents?: number;
  /**
   * `request`: the buyer is the pro's own client (backend `own_client`), so the
   * row is their buyer locking in a standing offer, not a marketplace lead:
   * no fee, the Requests card's "Confirm · no fee" action.
   */
  ownClient?: true;
  /** `request`: buyer tier (t1..t3). The request accepted that tier's standing offer. */
  buyerTier?: string;
  /** `request`: the buyer's target area, as the request carries it. Absent when not sent. */
  area?: string;
  /** `request`: the buyer's price bracket (a band, never an exact figure). */
  priceBracket?: { low: number; high: number };
  /** Signals: the one-line reason the Today queue already shows. */
  reason?: string;
  /** `follow_up`: due date has passed (Pacific calendar day). */
  overdue?: boolean;
  /** `follow_up`: the task's own due instant (ISO), null when undated; drives the due-date copy. */
  dueAt?: string | null;
  /** `follow_up`: the linked person's name (the subline's non-time context); absent when unlinked. */
  contactName?: string;
  /** Signals and follow-ups: the Today item key, for Done / Snooze. */
  itemKey?: string;
  /** `follow_up`: the CRM task id. */
  taskId?: string;
  /** Today rows: when the signal occurred / the task was created (the recency tie-break). */
  occurredAt?: string;
}

export interface NeedsStream {
  rows: NeedsStreamRow[];
  count: number;
}

export interface BuildNeedsStreamInput {
  today: TodayQueueVM | null;
  requests: readonly MarketplaceConnectionRequest[] | null;
  summary: ProHomeSummary | null;
  now: Date;
}

const URGENT_WINDOW_MS = 24 * 60 * 60 * 1000;
/** A held connection is released this many days after it was held (backend `HeldAbandonHorizon`). */
export const HELD_ABANDON_DAYS = 7;
const PACIFIC = "America/Los_Angeles";

/** Total-order tiebreak when deadlines are equal. */
const KIND_PRIORITY: Record<NeedsRowKind, number> = {
  held: 0,
  recovery: 1,
  request: 2,
  offer_expiring: 3,
  follow_up: 4,
  high_intent: 5,
  new_buyer: 6,
};

const PACIFIC_PARTS = new Intl.DateTimeFormat("en-US", {
  timeZone: PACIFIC,
  year: "numeric",
  month: "numeric",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
  hourCycle: "h23",
});

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function pacificWallClock(d: Date): WallClock {
  const out: Record<string, number> = {};
  for (const p of PACIFIC_PARTS.formatToParts(d)) {
    if (p.type !== "literal") out[p.type] = Number(p.value);
  }
  return out as unknown as WallClock;
}

/** Pacific's offset from UTC at `d`, in ms (negative; PDT -7h, PST -8h). */
function pacificOffsetMs(d: Date): number {
  const w = pacificWallClock(d);
  const wallAsUtc = Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second);
  return wallAsUtc - Math.floor(d.getTime() / 1000) * 1000;
}

/** Sortable key for the Pacific calendar day containing `d`. */
function pacificDayKey(d: Date): number {
  const w = pacificWallClock(d);
  return w.year * 10_000 + w.month * 100 + w.day;
}

/**
 * The instant the Pacific calendar day containing `d` ends (the next Pacific
 * midnight). DST-correct: the day is 23h or 25h long on the transition dates.
 * Midnight never falls inside the 2am transition gap, so two offset passes
 * settle on the right instant.
 */
export function endOfPacificDay(d: Date): Date {
  const w = pacificWallClock(d);
  const wall = Date.UTC(w.year, w.month - 1, w.day + 1, 0, 0, 0);
  const guess = wall - pacificOffsetMs(new Date(wall));
  return new Date(wall - pacificOffsetMs(new Date(guess)));
}

/**
 * Whole Pacific calendar days from `now` to `iso` (0 = same day, negative =
 * past); null when `iso` does not parse. The due-date copy reads this so a
 * follow-up due next month never claims "due today".
 */
export function pacificDayDiff(iso: string, now: Date): number | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const toDays = (d: Date) => {
    const w = pacificWallClock(d);
    return Date.UTC(w.year, w.month - 1, w.day) / 86_400_000;
  };
  return Math.round(toDays(new Date(t)) - toDays(now));
}

function parseInstant(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : new Date(t);
}

function deadlineUrgent(deadline: Date | null, now: Date): boolean {
  return deadline !== null && deadline.getTime() - now.getTime() <= URGENT_WINDOW_MS;
}

function requestRow(r: MarketplaceConnectionRequest, now: Date): NeedsStreamRow {
  const deadline = parseInstant(r.expires_at);
  // The pro's own buyer locking in a standing offer reads "Your buyer" with
  // the name the link shares and carries no fee (the backend quotes the $0
  // retention cause); a stranger stays an anonymous handle with the fee the
  // pro would be charged — even a $0 one from a full discount grant.
  const ownClient = isOwnClientRequest(r);
  return {
    kind: "request",
    ref: r.id,
    deadline: deadline?.toISOString() ?? null,
    urgent: deadlineUrgent(deadline, now),
    provenance: ownClient ? "client" : "marketplace",
    title: requestBuyerLabel(r),
    action: { href: `/marketplace/requests/${encodeURIComponent(r.id)}` },
    ...(ownClient ? { ownClient: true as const } : { feeCents: r.fee_quote?.net_cents }),
    buyerTier: r.buyer_tier,
    area: r.target_area || undefined,
    priceBracket: r.price_bracket
      ? { low: r.price_bracket.low, high: r.price_bracket.high }
      : undefined,
  };
}

function summaryRows(summary: ProHomeSummary, now: Date): NeedsStreamRow[] {
  const rows: NeedsStreamRow[] = [];

  const held = summary.held;
  if (held && held.count > 0) {
    const release = parseInstant(held.earliest_release_at);
    // Per-connection release times are not on the wire; every held row uses
    // the earliest. With no ids (should not happen) one row still stands for
    // the whole held set so the badge never reads zero while a card is failing.
    const ids = held.connection_ids.length > 0 ? held.connection_ids : ["held"];
    for (const id of ids) {
      rows.push({
        kind: "held",
        ref: id,
        deadline: release?.toISOString() ?? null,
        urgent: true,
        provenance: "marketplace",
        title: "Connection on hold",
        action: { href: "/settings#billing" },
      });
    }
  }

  for (const w of summary.recovery ?? []) {
    const closes = parseInstant(w.closes_at);
    rows.push({
      kind: "recovery",
      ref: w.window_id,
      deadline: closes?.toISOString() ?? null,
      urgent: true,
      provenance: "client",
      title: w.client_display_name,
      action: {
        href: `/marketplace/browse/recovery?recovery_window_id=${encodeURIComponent(w.window_id)}`,
      },
    });
  }

  for (const o of summary.offers?.expiring_24h ?? []) {
    const expires = parseInstant(o.expires_at);
    rows.push({
      kind: "offer_expiring",
      ref: o.offer_id,
      deadline: expires?.toISOString() ?? null,
      urgent: deadlineUrgent(expires, now),
      provenance: "marketplace",
      title: o.display_code,
      action: { href: "/marketplace/offers" },
    });
  }

  return rows;
}

function signalRow(
  item: TodaySignalVM,
  kind: "new_buyer" | "high_intent",
  eod: Date,
): NeedsStreamRow {
  const client = item.client;
  return {
    kind,
    ref: item.itemKey,
    deadline: eod.toISOString(),
    urgent: false,
    // Today links are always active; the Today adapter does not carry the
    // connection method on every path, so anything not marketplace is invited.
    provenance: client.connection?.method === "marketplace" ? "marketplace" : "invited",
    title: client.name,
    action: { href: personRoute(client.contactId, `/clients/${encodeURIComponent(client.id)}`) },
    reason: item.reason,
    itemKey: item.itemKey,
    occurredAt: item.occurredAt,
  };
}

function followUpRow(item: TodayTaskItemVM, now: Date): NeedsStreamRow {
  const t = item.task;
  const due = parseInstant(t.dueAt);
  // A due date is a Pacific calendar day: it ends at that day's Pacific
  // midnight, and it is overdue once Pacific "today" is later than that day.
  const deadline = due ? endOfPacificDay(due) : null;
  return {
    kind: "follow_up",
    ref: t.id,
    deadline: deadline?.toISOString() ?? null,
    urgent: due !== null && pacificDayKey(due) < pacificDayKey(now),
    provenance: "book",
    title: t.title,
    action: { href: personRoute(t.contact?.id, "/tasks") },
    overdue: due !== null && pacificDayKey(due) < pacificDayKey(now),
    dueAt: due ? (t.dueAt ?? null) : null,
    contactName: t.contact?.name || undefined,
    itemKey: item.itemKey,
    taskId: t.id,
    occurredAt: t.createdAt,
  };
}

function todayRows(today: TodayQueueVM, now: Date): NeedsStreamRow[] {
  const eod = endOfPacificDay(now);
  const rows: NeedsStreamRow[] = [];
  for (const group of today.groups) {
    for (const item of group.items) {
      if (item.kind === "signal") {
        if (group.key === "new") rows.push(signalRow(item, "new_buyer", eod));
        else if (group.key === "high_intent") rows.push(signalRow(item, "high_intent", eod));
      } else if (group.key === "your_followups" && !item.task.doneAt) {
        rows.push(followUpRow(item, now));
      }
    }
  }
  return rows;
}

/** Recency for the tie-break: epoch ms of `occurredAt`, 0 when absent or unparsable. */
function recencyOf(row: NeedsStreamRow): number {
  const t = row.occurredAt ? Date.parse(row.occurredAt) : NaN;
  return Number.isNaN(t) ? 0 : t;
}

const CRM_KINDS: ReadonlySet<NeedsRowKind> = new Set(["new_buyer", "high_intent", "follow_up"]);

export function buildNeedsStream({ today, requests, summary, now }: BuildNeedsStreamInput): NeedsStream {
  const candidates: NeedsStreamRow[] = [];

  for (const r of requests ?? []) candidates.push(requestRow(r, now));
  if (summary) candidates.push(...summaryRows(summary, now));
  if (today) candidates.push(...todayRows(today, now));

  // Dedupe by (kind, ref); first occurrence wins.
  const seen = new Set<string>();
  const rows = candidates.filter((row) => {
    const key = `${row.kind}\u0000${row.ref}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  // Order: urgent rows first, then by deadline ascending (no deadline last).
  // Equal deadlines: among today's CRM rows (which share end-of-day Pacific)
  // the newer one first, as the spec's recency tie-break requires; then kind
  // priority (held, recovery, request, offer_expiring, follow_up, high_intent,
  // new_buyer), then ref ascending. Every non-CRM kind outranks every CRM kind
  // in the priority table, so the three-level rule is a consistent total
  // order and equal inputs never reorder between renders.
  rows.sort((a, b) => {
    if (a.urgent !== b.urgent) return a.urgent ? -1 : 1;
    const da = a.deadline === null ? Infinity : Date.parse(a.deadline);
    const db = b.deadline === null ? Infinity : Date.parse(b.deadline);
    if (da !== db) return da < db ? -1 : 1;
    if (CRM_KINDS.has(a.kind) && CRM_KINDS.has(b.kind)) {
      const ra = recencyOf(a);
      const rb = recencyOf(b);
      if (ra !== rb) return rb - ra;
    }
    const pa = KIND_PRIORITY[a.kind];
    const pb = KIND_PRIORITY[b.kind];
    if (pa !== pb) return pa - pb;
    return a.ref < b.ref ? -1 : a.ref > b.ref ? 1 : 0;
  });

  return { rows, count: rows.length };
}
